import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Star, Plus, Pencil, Trash2, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export const TestimonialsAdmin = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', company: '', text: '', rating: 5, is_visible: true });

  const fetchTestimonials = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/testimonials`);
      setTestimonials(res.data);
    } catch (err) {
      toast.error('Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTestimonials(); }, [fetchTestimonials]);

  const resetForm = () => {
    setForm({ name: '', company: '', text: '', rating: 5, is_visible: true });
    setEditing(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.text) {
      toast.error('Name and review text are required');
      return;
    }
    try {
      if (editing) {
        await axios.put(`${API}/api/testimonials/${editing}`, form);
        toast.success('Testimonial updated');
      } else {
        await axios.post(`${API}/api/testimonials`, form);
        toast.success('Testimonial added');
      }
      resetForm();
      fetchTestimonials();
    } catch (err) {
      toast.error('Failed to save testimonial');
    }
  };

  const handleEdit = (t) => {
    setForm({ name: t.name, company: t.company || '', text: t.text, rating: t.rating, is_visible: t.is_visible });
    setEditing(t.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try {
      await axios.delete(`${API}/api/testimonials/${id}`);
      toast.success('Testimonial deleted');
      fetchTestimonials();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const toggleVisibility = async (t) => {
    try {
      await axios.put(`${API}/api/testimonials/${t.id}`, { is_visible: !t.is_visible });
      fetchTestimonials();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-44 pb-12 px-4">
      <div className="container mx-auto max-w-4xl">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin/tools">
              <Button variant="outline" size="sm"><ArrowLeft size={16} className="mr-1" /> Back</Button>
            </Link>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="testimonials-admin-title">Manage Testimonials</h1>
          </div>
          <Button onClick={() => { resetForm(); setShowForm(!showForm); }} data-testid="add-testimonial-btn">
            <Plus size={16} className="mr-1" /> Add Review
          </Button>
        </div>

        {showForm && (
          <Card className="mb-8 border-2 border-blue-200">
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Customer Name *</Label>
                    <Input data-testid="testimonial-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g. John Lim" />
                  </div>
                  <div>
                    <Label>Company (optional)</Label>
                    <Input data-testid="testimonial-company-input" value={form.company} onChange={e => setForm({...form, company: e.target.value})} placeholder="e.g. ABC Construction" />
                  </div>
                </div>
                <div>
                  <Label>Review Text *</Label>
                  <textarea
                    data-testid="testimonial-text-input"
                    className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={form.text}
                    onChange={e => setForm({...form, text: e.target.value})}
                    placeholder="Write the customer's review..."
                  />
                </div>
                <div className="flex items-center gap-4">
                  <div>
                    <Label>Rating</Label>
                    <div className="flex gap-1 mt-1">
                      {[1,2,3,4,5].map(star => (
                        <button key={star} type="button" onClick={() => setForm({...form, rating: star})}
                          className={`${star <= form.rating ? 'text-yellow-400' : 'text-gray-300'}`}>
                          <Star size={24} fill={star <= form.rating ? 'currentColor' : 'none'} />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    <input type="checkbox" checked={form.is_visible} onChange={e => setForm({...form, is_visible: e.target.checked})} id="visible-check" />
                    <Label htmlFor="visible-check">Visible on website</Label>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button type="submit" data-testid="testimonial-save-btn">{editing ? 'Update' : 'Add'} Testimonial</Button>
                  <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <p className="text-center text-gray-500 py-12">Loading...</p>
        ) : testimonials.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <Star size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium">No testimonials yet</p>
            <p className="text-sm">Click "Add Review" to create your first customer testimonial</p>
          </div>
        ) : (
          <div className="space-y-4">
            {testimonials.map(t => (
              <Card key={t.id} data-testid={`testimonial-card-${t.id}`} className={`${!t.is_visible ? 'opacity-60 border-dashed' : ''}`}>
                <CardContent className="pt-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold text-gray-900">{t.name}</p>
                        {t.company && <span className="text-sm text-gray-500">- {t.company}</span>}
                        {!t.is_visible && <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">Hidden</span>}
                      </div>
                      <div className="flex gap-0.5 mb-2">
                        {[...Array(t.rating)].map((_, i) => (
                          <Star key={i} size={14} className="text-yellow-400 fill-yellow-400" />
                        ))}
                      </div>
                      <p className="text-gray-600 text-sm italic">"{t.text}"</p>
                    </div>
                    <div className="flex gap-1 ml-4">
                      <Button variant="ghost" size="sm" onClick={() => toggleVisibility(t)} title={t.is_visible ? 'Hide' : 'Show'}>
                        {t.is_visible ? <Eye size={16} /> : <EyeOff size={16} />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(t)} data-testid={`edit-testimonial-${t.id}`}>
                        <Pencil size={16} />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(t.id)} data-testid={`delete-testimonial-${t.id}`}>
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TestimonialsAdmin;
